import { useState } from "react";
import { ArrowUpTrayIcon, DocumentTextIcon, EyeIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, Card, FileUpload, Modal } from "../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { useScholarshipChange } from "./model";
import { TYPE_NAMES, formatDateTime, formatSize, openDocument } from "./review/model";
import { canManageScholarships } from "./access";

// The evidence a scholarship carries: its field in the scholarship, its endpoint and how the screens name it
export const EVIDENCE = {
  approval: {
    field: "approvalEvidence",
    path: "approval-evidence",
    title: "Approval evidence",
    what: "The DG's signed letter approving the call, or the call letter received from MINAFET.",
    needed: "Needed to publish.",
  },
  privacy: {
    field: "privacyEvidence",
    path: "privacy-evidence",
    title: "Reason for private visibility",
    what: "The document explaining why only invited students may apply.",
    needed: "Needed to publish a private scholarship, or to make a published one private.",
  },
};

// The upload form: the file only
function UploadDialog({ scholarship, kind, onClose }) {
  const change = useScholarshipChange(scholarship.id);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) {
      setErrors({ file: "Choose the file." });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await change((etag) => scholarshipsApi.post(`admin/scholarships/${scholarship.id}/${kind.path}`, body, { ifMatch: etag }));
      toast.success(`${kind.title} uploaded`);
      onClose();
    } catch (error) {
      console.error(`Failed to upload the ${kind.path} of scholarship ${scholarship.id}:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to upload the file. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${scholarship[kind.field] ? "Replace" : "Upload"}: ${kind.title.toLowerCase()}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={submit} loading={busy}>
            Upload
          </Button>
        </>
      }
    >
      <FileUpload label={kind.title} required info={kind.what} accept="application/pdf,image/jpeg,image/png" hint="PDF, JPEG or PNG, up to 10 MB" file={file} onChange={setFile} error={errors.file} />
    </Modal>
  );
}

/**
 * One evidence file of a scholarship: HEC Admins upload or replace it, staff can view it. While a decided
 * scholarship is being reopened, its reason for private visibility can still be replaced.
 */
export default function EvidenceCard({ scholarship, kind, reopening = false }) {
  const [uploading, setUploading] = useState(false);
  const evidence = scholarship[kind.field];
  const decidedLocked = scholarship.state === "DECIDED" && !(reopening && kind === EVIDENCE.privacy);
  const editable = canManageScholarships() && scholarship.state !== "CANCELLED" && !decidedLocked;

  return (
    <Card title={kind.title} subtitle={`${kind.what} ${kind.needed}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {evidence ? (
          <div className="flex min-w-0 items-center gap-3">
            <DocumentTextIcon className="h-6 w-6 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-800">{evidence.fileName}</p>
              <p className="text-xs text-slate-500">
                {TYPE_NAMES[evidence.contentType] ?? "File"} · {formatSize(evidence.sizeBytes)} · uploaded {formatDateTime(evidence.uploadedAt)}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Not uploaded yet.</p>
        )}
        <div className="flex shrink-0 gap-2">
          {evidence && (
            <Button variant="outline" icon={<EyeIcon className="h-4 w-4" />} onClick={() => openDocument(`staff/scholarships/${scholarship.id}/${kind.path}/download-url`)}>
              View
            </Button>
          )}
          {editable && (
            <Button variant={evidence ? "outline" : "solid"} icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => setUploading(true)}>
              {evidence ? "Replace" : "Upload"}
            </Button>
          )}
        </div>
      </div>
      {uploading && <UploadDialog scholarship={scholarship} kind={kind} onClose={() => setUploading(false)} />}
    </Card>
  );
}
